import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import {
  Modal,
  Form,
  Select,
  Switch,
  Input,
  InputNumber,
  DatePicker,
  Segmented,
  Button,
} from 'antd';

import {
  createSession,
  selectors,
  utils,
  CASH_APPROACH,
  RISK_WEIGHTED_APPROACH,
  PORTFOLIO_MANAGEMENT,
  WEALTH_MANAGEMENT,
  REAL_TIME_SCENARIO,
  HISTORICAL_SCENARIO,
  NORMAL_TIME,
  REPORTING_CURRENCIES,
  ReplaySessionDefaults,
  ReplayTargetKlass,
} from 'state';
import { PercentInput } from 'components';

import { Card, Field } from './components';
import './createSessionStudio.css';

type Props = {
  visible: boolean;
  hide: () => void;
  klassId: number;
  replayDefaults?: ReplaySessionDefaults;
  replaySourceSessionId?: number;
  // Admin cross-trainer replay: when true, the modal renders a Trainer -> Class
  // target picker and uses the chosen class as `klass_id` instead of `klassId`.
  // Trainer callers omit both props and behave exactly as before.
  allowKlassSelect?: boolean;
  klassOptions?: ReplayTargetKlass[];
};

const getNow = () => dayjs().startOf('day');

/**
 * initialValues mirror the classic SessionSettings modal's defaults so the
 * dispatched payload matches. IMPORTANT: this modal reads values via
 * getFieldsValue(true), which also returns initialValues-only keys — so, unlike
 * the classic modal, we must NOT seed `metricsInterval` here. The backend Session
 * model dropped that field, and sending it makes self.create(**data) raise
 * TypeError; with it absent, v.metricsInterval is undefined and JSON drops the
 * (type-required) metrics_interval key. (limit_metric is fine — never seeded.)
 */
const initialValues = {
  scenarioType: REAL_TIME_SCENARIO,
  riskApproach: CASH_APPROACH,
  managementType: PORTFOLIO_MANAGEMENT,

  limitOrderCollateral: 10,
  riskWeightedLimit: 100_000_000,
  transactionFee: 0,
  short_sell_leverage: 30,
  fxLeverage: 100,
  bondLeverage: 100,
  initialAmount: 100_000_000,
  reportingCurrency: 'USD',
  allowShortSell: true,
  clientMoney: false,
  showAuctions: false,
  tradingTimeType: NORMAL_TIME,
  dates: [getNow().subtract(1, 'months'), getNow()] as [Dayjs, Dayjs],
  newsImportances: '1,2',
  frequency: 1,
  passTime: 180,
  delay: 0,
  clientMoneyScenario: null as number | null,
  perSectorAlpha: false,
};

const toDayjsRange = (
  startDate: string | null,
  endDate: string | null,
): [Dayjs, Dayjs] | undefined => {
  if (!startDate || !endDate) return undefined;
  return [dayjs(startDate), dayjs(endDate)];
};

// Replay mirrors only the fields this modal owns (same set as the classic one).
const mapReplayDefaultsToFormValues = (defaults: ReplaySessionDefaults) => ({
  scenarioType: defaults.scenario_type,
  riskApproach: defaults.risk_approach,
  managementType: defaults.management_type,
  limitOrderCollateral: defaults.limit_order_collateral * 100,
  riskWeightedLimit: defaults.risk_weighted_limit,
  short_sell_leverage: defaults.short_sell_leverage * 100,
  fxLeverage: defaults.fx_leverage * 100,
  bondLeverage: defaults.bond_leverage * 100,
  initialAmount: defaults.starting_cash_amount,
  reportingCurrency: defaults.reporting_currency,
  allowShortSell: defaults.allow_short_sell,
  clientMoney: defaults.client_money,
  showAuctions: defaults.show_auctions,
  tradingTimeType: defaults.trading_time_type,
  dates: (toDayjsRange(defaults.start_date, defaults.end_date) ||
    initialValues.dates) as [Dayjs, Dayjs],
  newsImportances: defaults.news_importances.join(','),
  frequency: defaults.frequency,
  delay: defaults.delay_minutes_left,
  clientMoneyScenario: defaults.client_money_scenario,
  passTime: defaults.historical_sleep_time,
  perSectorAlpha: false,
});

const moneyFormatter = (value: number | string | undefined) =>
  ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const moneyParser = (value: string | undefined) =>
  Number(value?.replace(/\$\s?|(,*)/g, '') || 0);

// Info-tooltip copy disabled until better text is written. Re-add the
// `info={…}` prop on the matching Fields below to restore the (i) circles.
// const NEWS_INFO =
//   'Which news-importance tiers players receive. Defaults to 1 and 2.';
// const PER_SECTOR_INFO =
//   'Track and grade alpha per market sector (historical sessions only).';
// const CLIENT_DEALING_INFO =
//   'Run a client-money scenario — its dates, AUM and cadence drive the schedule.';

const CreateSessionStudio: React.FC<Props> = ({
  visible,
  hide,
  klassId,
  replayDefaults,
  replaySourceSessionId,
  allowKlassSelect = false,
  klassOptions,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // Admin cross-trainer replay: destination trainer + class the new session lands
  // in. Two dependent dropdowns (pick trainer -> then that trainer's classes). Not
  // Form fields (the target lives outside the copied-settings form). Default to none
  // so the admin must consciously pick a destination before Create unlocks.
  const [targetTrainerId, setTargetTrainerId] = React.useState<
    number | undefined
  >(undefined);
  const [targetKlassId, setTargetKlassId] = React.useState<number | undefined>(
    undefined,
  );

  // Distinct trainers from the flat class list.
  const trainerOptions = useMemo(() => {
    const byId = new Map<number, string>();
    (klassOptions || []).forEach(k => {
      if (!byId.has(k.trainer_id)) {
        byId.set(k.trainer_id, k.trainer_username || '(no trainer)');
      }
    });
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [klassOptions]);

  // Classes belonging to the currently selected trainer.
  const klassOptionsForTrainer = useMemo(() => {
    if (targetTrainerId == null) return [];
    return (klassOptions || [])
      .filter(k => k.trainer_id === targetTrainerId)
      .map(k => ({ value: k.id, label: `${k.name} (#${k.id})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [klassOptions, targetTrainerId]);

  const createSessionWaiting = useSelector(selectors.createSessionWaiting);
  const createSessionSucceeded = useSelector(selectors.createSessionSucceeded);
  const createSessionFailed = useSelector(selectors.createSessionFailed);
  // Select the indexed object (a stable redux reference) and derive the array
  // with useMemo, so `scenarios` keeps a stable identity across re-renders —
  // without it the auto-fill effect below would loop (fresh array every render
  // → effect re-runs → setFieldsValue → re-render → …).
  const scenariosById = useSelector(selectors.ClientDealingScenarios);
  const scenarios = useMemo(
    () => Object.values(scenariosById),
    [scenariosById],
  );

  // Latch so the "succeeded → close" effect only fires for OUR submit.
  const [submitted, setSubmitted] = React.useState(false);

  // Live form values that drive conditional sections / the recap.
  const scenarioType =
    Form.useWatch('scenarioType', form) ?? initialValues.scenarioType;
  const riskApproach =
    Form.useWatch('riskApproach', form) ?? initialValues.riskApproach;
  const managementType =
    Form.useWatch('managementType', form) ?? initialValues.managementType;
  const reportingCurrency =
    Form.useWatch('reportingCurrency', form) ?? initialValues.reportingCurrency;
  const clientMoney = Form.useWatch('clientMoney', form) ?? false;
  const allowShortSell =
    Form.useWatch('allowShortSell', form) ?? initialValues.allowShortSell;
  const clientMoneyScenario = Form.useWatch('clientMoneyScenario', form);
  const dates = Form.useWatch('dates', form);
  const frequency = Form.useWatch('frequency', form);
  const passTime = Form.useWatch('passTime', form);

  const isHistorical = scenarioType === HISTORICAL_SCENARIO;
  const isCash = riskApproach === CASH_APPROACH;
  const isReplay = Boolean(replayDefaults && replaySourceSessionId);

  // Reset / prefill on open.
  useEffect(() => {
    if (!visible) {
      setSubmitted(false);
      setTargetTrainerId(undefined);
      setTargetKlassId(undefined);
      return;
    }
    setTargetTrainerId(undefined);
    setTargetKlassId(undefined);
    if (replayDefaults) {
      form.setFieldsValue(mapReplayDefaultsToFormValues(replayDefaults));
    } else {
      form.resetFields();
    }
  }, [visible, replayDefaults, form]);

  // Close once OUR create settles successfully (error toast comes from the
  // reducer; a failed attempt just unlatches so the user can retry).
  useEffect(() => {
    if (!submitted) return;
    if (createSessionSucceeded) {
      setSubmitted(false);
      hide();
    } else if (createSessionFailed) {
      setSubmitted(false);
    }
  }, [submitted, createSessionSucceeded, createSessionFailed, hide]);

  // Client-dealing auto-fill: a picked scenario owns the dates / AUM / cadence,
  // and those inputs lock (mirrors the classic modal's render-prop side effect).
  useEffect(() => {
    if (!clientMoney || clientMoneyScenario == null) return;
    const sel = scenarios.find(s => s.id === clientMoneyScenario);
    if (!sel) return;
    form.setFieldsValue({
      dates: [
        dayjs(sel.first_date_in_session, 'YYYY-MM-DD'),
        dayjs(sel.session_end_date, 'YYYY-MM-DD'),
      ],
      initialAmount: sel.aum,
      frequency: sel.period_frequency,
    });
  }, [clientMoney, clientMoneyScenario, scenarios, form]);

  // Estimated wall-clock session length, "Xh:Ym" (historical only).
  const sessionDuration = useMemo(() => {
    if (!isHistorical) return '—';
    if (!frequency || typeof frequency !== 'number') return '—';
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return '—';
    const workdays = utils.workdayCount(dates[0], dates[1], frequency);
    const seconds = workdays * (passTime || 0);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds / 60) % 60);
    return `${h}h ${m}m`;
  }, [isHistorical, dates, frequency, passTime]);

  const onValuesChange = (changed: Record<string, unknown>) => {
    // Per-sector alpha is historical-only; clear it when leaving historical.
    if (
      'scenarioType' in changed &&
      changed.scenarioType !== HISTORICAL_SCENARIO
    ) {
      form.setFieldsValue({ perSectorAlpha: false });
    }
  };

  const onFinish = () => {
    if (createSessionWaiting) return;
    // Admin cross-trainer replay must have a destination class chosen first.
    if (allowKlassSelect && targetKlassId == null) return;
    const v = form.getFieldsValue(true);
    setSubmitted(true);
    dispatch(
      createSession({
        klass_id: allowKlassSelect ? (targetKlassId as number) : klassId,
        scenario_type: v.scenarioType,
        limit_order_collateral:
          (v.limitOrderCollateral || initialValues.limitOrderCollateral) / 100,
        transaction_fee: 0,
        short_sell_leverage:
          (v.short_sell_leverage ?? initialValues.short_sell_leverage) / 100,
        fx_leverage: (v.fxLeverage || initialValues.fxLeverage) / 100,
        bond_leverage: (v.bondLeverage || initialValues.bondLeverage) / 100,
        starting_cash_amount: v.initialAmount || initialValues.initialAmount,
        reporting_currency:
          v.reportingCurrency || initialValues.reportingCurrency,
        // Key kept only to satisfy the CreateSessionPayload type. metrics_interval
        // was removed from the backend model; `metricsInterval` is deliberately
        // NOT seeded in initialValues, so v.metricsInterval is undefined and
        // JSON.stringify drops it (sending it would crash self.create(**data)).
        metrics_interval: v.metricsInterval,
        start_date: (v.dates || [])[0]?.format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
        end_date: (v.dates || [])[1]?.format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
        risk_weighted_limit:
          v.riskWeightedLimit || initialValues.riskWeightedLimit,
        risk_approach: v.riskApproach,
        // Role picker is hidden for now — always Portfolio (Wealth unused).
        management_type: v.managementType ?? PORTFOLIO_MANAGEMENT,
        trading_time_type: v.tradingTimeType,
        spread_widener: 0,
        allow_short_sell: v.allowShortSell,
        client_money: v.clientMoney || initialValues.clientMoney,
        show_auctions: v.showAuctions || initialValues.showAuctions,
        news_importances:
          v.newsImportances?.replaceAll(' ', '').split(',') || [],
        frequency: v.frequency || initialValues.frequency,
        delay_minutes_left: v.delay || initialValues.delay,
        limit_metric: v.limitMetric,
        client_money_scenario: v.clientMoneyScenario,
        historical_sleep_time: v.passTime,
        per_sector_alpha:
          v.scenarioType === HISTORICAL_SCENARIO
            ? Boolean(v.perSectorAlpha)
            : false,
        replay_source_session_id: replaySourceSessionId,
      }),
    );
  };

  const recap = [
    scenarioType === HISTORICAL_SCENARIO ? 'Historical' : 'Real Time',
    isCash ? 'Cash' : 'Risk-weighted',
    managementType === WEALTH_MANAGEMENT ? 'Wealth' : 'Portfolio',
    reportingCurrency,
  ].join('  ·  ');

  return (
    <Modal
      open={visible}
      onCancel={() => {
        if (!createSessionWaiting) hide();
      }}
      closable={!createSessionWaiting}
      maskClosable={!createSessionWaiting}
      keyboard={!createSessionWaiting}
      width={920}
      className="csv2"
      styles={{
        container: { padding: 0, borderRadius: 16, overflow: 'hidden' },
        header: {
          margin: 0,
          padding: '16px 22px',
          borderBottom: '1px solid #e6ecf5',
          background: '#fff',
        },
        body: {
          padding: '18px 22px',
          background: 'linear-gradient(180deg, #f7f9fc 0%, #eef3f8 100%)',
        },
        footer: {
          margin: 0,
          padding: '14px 22px',
          borderTop: '1px solid #e6ecf5',
          background: '#fff',
        },
      }}
      title={
        <div className="csv2-title">
          <span className="csv2-title__main">
            {isReplay
              ? `Replay session #${replaySourceSessionId}`
              : 'New session'}
          </span>
          <span className="csv2-title__sub">
            {isReplay
              ? 'Prefilled from the source — tune anything before launch'
              : 'Configure the simulation before it goes live'}
          </span>
        </div>
      }
      footer={
        <div className="csv2-foot">
          <span className="csv2-foot__recap">{recap}</span>
          <Button
            type="primary"
            className="csv2-create"
            loading={createSessionWaiting}
            disabled={
              createSessionWaiting ||
              (allowKlassSelect && targetKlassId == null)
            }
            onClick={() => form.submit()}
          >
            {isReplay ? 'Create replay' : 'Create session'}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        component={false}
      >
        {/* Admin cross-trainer replay: destination trainer + class. Lives above the
            copied-settings form because it is NOT a copied setting — it decides where
            the replayed session is created. */}
        {allowKlassSelect && (
          <div
            className="csv2-mode"
            style={{ marginBottom: 12, display: 'flex', gap: 12 }}
          >
            <Field
              label="Replay into — trainer"
              hint="Destination trainer for the new session"
            >
              <Select
                style={{ width: '100%' }}
                placeholder="Select trainer"
                value={targetTrainerId}
                onChange={val => {
                  setTargetTrainerId(val);
                  setTargetKlassId(undefined); // class list depends on trainer
                }}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={trainerOptions}
                status={targetTrainerId == null ? 'warning' : undefined}
              />
            </Field>
            <Field
              label="Replay into — class"
              hint="Class owned by the selected trainer"
            >
              <Select
                style={{ width: '100%' }}
                placeholder={
                  targetTrainerId == null
                    ? 'Select a trainer first'
                    : 'Select class'
                }
                value={targetKlassId}
                onChange={val => setTargetKlassId(val)}
                disabled={targetTrainerId == null}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={klassOptionsForTrainer}
                status={
                  targetTrainerId != null && targetKlassId == null
                    ? 'warning'
                    : undefined
                }
              />
            </Field>
          </div>
        )}

        {/* Mode switch — the headline decision, full width. */}
        <div className="csv2-mode">
          <Form.Item name="scenarioType" noStyle>
            <Segmented
              block
              size="large"
              options={[
                {
                  value: REAL_TIME_SCENARIO,
                  label: (
                    <div className="csv2-mode__opt">
                      <span className="csv2-mode__opt-title">Real time</span>
                      <span className="csv2-mode__opt-sub">
                        Live market, trades the clock
                      </span>
                    </div>
                  ),
                },
                {
                  value: HISTORICAL_SCENARIO,
                  label: (
                    <div className="csv2-mode__opt">
                      <span className="csv2-mode__opt-title">Historical</span>
                      <span className="csv2-mode__opt-sub">
                        Replay a past window, period by period
                      </span>
                    </div>
                  ),
                },
              ]}
            />
          </Form.Item>
        </div>

        <div className="csv2-grid">
          {/* ---- Left column: mandate + capital --------------------------- */}
          <div className="csv2-col">
            <Card title="Mandate">
              {/* Role (management_type) is hidden for now — Wealth Management
                  isn't in active use. The payload still defaults to Portfolio
                  via `managementType: PORTFOLIO_MANAGEMENT` in initialValues
                  (getFieldsValue(true) returns it even though no control is
                  mounted). Restore the <Field> below to bring the picker back. */}
              <Field label="Risk approach" hint="How buying power is gated">
                <Form.Item name="riskApproach" noStyle>
                  <Segmented
                    options={[
                      { value: CASH_APPROACH, label: 'Cash' },
                      { value: RISK_WEIGHTED_APPROACH, label: 'Risk-weighted' },
                    ]}
                  />
                </Form.Item>
              </Field>
              <Field label="Reporting currency">
                <Form.Item name="reportingCurrency" noStyle>
                  <Select
                    style={{ width: 110 }}
                    options={REPORTING_CURRENCIES.map(ccy => ({
                      value: ccy,
                      label: ccy,
                    }))}
                  />
                </Form.Item>
              </Field>
              <Field
                label="Allow short selling"
                hint="Let players hold negative positions"
              >
                <Form.Item
                  name="allowShortSell"
                  noStyle
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Field>
            </Card>

            <Card title={isCash ? 'Capital' : 'Risk budget'}>
              {isCash ? (
                <>
                  <Field label="Initial capital">
                    <Form.Item name="initialAmount" noStyle>
                      <InputNumber
                        disabled={clientMoney}
                        formatter={moneyFormatter}
                        parser={moneyParser}
                        addonBefore="$"
                        step={10_000_000}
                        min={0}
                        style={{ width: 200 }}
                      />
                    </Form.Item>
                  </Field>
                  {/* Limit-order collateral, FX leverage and Bond leverage are
                      hidden for now. The payload still carries their defaults via
                      initialValues (limitOrderCollateral: 10, fxLeverage: 100,
                      bondLeverage: 100 → 0.1 / 1.0 / 1.0); getFieldsValue(true)
                      returns them even with no control mounted. Restore the
                      <Field>s to bring the inputs back. */}
                  <Field
                    label="Short selling limit"
                    hint={
                      allowShortSell
                        ? 'Max short exposure, % of AUM'
                        : 'Enable short selling to set a limit'
                    }
                  >
                    <Form.Item name="short_sell_leverage" noStyle>
                      <PercentInput
                        style={{ width: 110 }}
                        min={0}
                        max={100}
                        disabled={!allowShortSell}
                      />
                    </Form.Item>
                  </Field>
                  {/* FX leverage / Bond leverage hidden — see note above. */}
                </>
              ) : (
                <Field
                  label="Risk-weighted limit"
                  hint="Cap on the book's risk-weighted exposure"
                >
                  <Form.Item name="riskWeightedLimit" noStyle>
                    <InputNumber
                      formatter={moneyFormatter}
                      parser={moneyParser}
                      style={{ width: 200 }}
                    />
                  </Form.Item>
                </Field>
              )}
            </Card>
          </div>

          {/* ---- Right column: scenario-specific timing & behaviour ------- */}
          <div className="csv2-col">
            <Card
              title="Schedule & cadence"
              className={!isHistorical ? 'csv2-card--disabled' : undefined}
              extra={
                isHistorical ? (
                  <span className="csv2-durchip">≈ {sessionDuration}</span>
                ) : (
                  <span className="csv2-histonly">Historical only</span>
                )
              }
            >
              <Field
                label="Historical window"
                hint={
                  clientMoney
                    ? 'Set by the client-money scenario'
                    : 'Weekends are unavailable'
                }
              >
                <Form.Item
                  name="dates"
                  noStyle
                  rules={[{ required: true, message: 'Pick a window' }]}
                >
                  <DatePicker.RangePicker
                    format="YYYY-MM-DD"
                    // Snug fixed width: comfortably fits two YYYY-MM-DD dates +
                    // separator + icon (≈86px/input) while leaving the label room.
                    style={{ width: 252 }}
                    disabled={clientMoney}
                    disabledDate={d => d.day() === 0 || d.day() === 6}
                  />
                </Form.Item>
              </Field>
              <Field label="Frequency" hint="Simulated days per period">
                <Form.Item name="frequency" noStyle>
                  <InputNumber
                    min={1}
                    max={31}
                    disabled={clientMoney}
                    addonAfter="days"
                    style={{ width: 130 }}
                  />
                </Form.Item>
              </Field>
              <Field
                label="Period duration"
                hint="Wall-clock seconds per period"
              >
                <Form.Item name="passTime" noStyle>
                  <InputNumber
                    min={20}
                    max={2000}
                    addonAfter="s"
                    style={{ width: 130 }}
                  />
                </Form.Item>
              </Field>
              {/* Start delay hidden for now — delay_minutes_left still
                      defaults to 0 via initialValues. Restore the <Field> to
                      bring the input back. */}
            </Card>

            <Card
              title="Market & behaviour"
              className={!isHistorical ? 'csv2-card--disabled' : undefined}
              extra={
                !isHistorical ? (
                  <span className="csv2-histonly">Historical only</span>
                ) : undefined
              }
            >
              <Field label="Client dealing">
                <Form.Item name="clientMoney" noStyle valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Field>
              {clientMoney && (
                <Field label="Scenario">
                  <Form.Item
                    name="clientMoneyScenario"
                    noStyle
                    rules={[{ required: true, message: 'Pick a scenario' }]}
                  >
                    <Select
                      placeholder="Select a scenario"
                      style={{ width: 240 }}
                      options={scenarios.map(s => ({
                        value: s.id,
                        label: s.name,
                      }))}
                    />
                  </Form.Item>
                </Field>
              )}
              <Field label="Enable auctions">
                <Form.Item name="showAuctions" noStyle valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Field>
              <Field label="Per-sector alpha">
                <Form.Item
                  name="perSectorAlpha"
                  noStyle
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Field>
              <Field label="News importance">
                <Form.Item name="newsImportances" noStyle>
                  <Input style={{ width: 130 }} />
                </Form.Item>
              </Field>
            </Card>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateSessionStudio;
